import { Autocomplete } from '@zeiss/lib-components';
import { useDebounce } from '@zeiss/lib-helpers';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { AutoCompleteLoading } from 'src/components/shared/AutoCompleteLoading';
import { DEBOUNCE_DELAY } from 'src/constants';
import { useOrganization } from 'src/hooks/api/useOrganization';
import { getOrganizationInfo } from 'src/modules/TenantManagement/components/OrganizationSearch';
import { OrganizationResponse } from 'src/types/rest/tenant';

interface OrganizationSearchInputProps {
  handleOrganizationSelect: (
    selectedOrganization: OrganizationResponse | null,
  ) => void;
  selectedOrganization: OrganizationResponse | null;
}

export const OrganizationSearch = ({
  handleOrganizationSelect,
  selectedOrganization,
}: OrganizationSearchInputProps) => {
  const { t } = useTranslation(['common']);

  const initialHelperText = t('common:input.optionalHelperText');

  const [helperText, setHelperText] = React.useState(initialHelperText);

  const [searchTerm, setSearchTerm] = React.useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, DEBOUNCE_DELAY);

  const { data, isPending } = useOrganization(debouncedSearchTerm);

  const options = React.useMemo(() => {
    let result: string[] = [];

    if (data) {
      result = data.items.map((item) => getOrganizationInfo(item));
    }
    return result;
  }, [data]);

  React.useEffect(() => {
    setSearchTerm(getOrganizationInfo(selectedOrganization));
  }, [selectedOrganization]);

  React.useEffect(() => {
    if (!isPending) {
      if (options.length > 0) {
        setHelperText(t('common:input.searchHelperText'));
      }
      if (
        options.length === 0 &&
        searchTerm !== getOrganizationInfo(selectedOrganization)
      ) {
        setHelperText(
          t('common:emptyState.searchNoX', {
            x: t('common:glossary.organization'),
          }),
        );
      }
    }
    if (searchTerm === getOrganizationInfo(selectedOrganization)) {
      setHelperText(initialHelperText);
    }
  }, [
    initialHelperText,
    isPending,
    options,
    searchTerm,
    selectedOrganization,
    t,
  ]);

  const handleOrganization = (value: string) => {
    setSearchTerm(value);

    if (data) {
      const result = data.items.find(
        (item) => getOrganizationInfo(item) === value,
      );
      if (result) handleOrganizationSelect(result);
    }

    // Remove selected organization by clicking X in the input
    if (value === '') handleOrganizationSelect(null);
  };

  /**
   * NOTE: Beyond Online React Workaround
   * The current (v4.31.0) implementation of the `AutoComplete` component only
   * shows the loading state if the `loading` prop is set to true **and** there
   * is no text in the input field.
   * This workaround uses the `emptyState` prop to show a loading spinner
   * when the user is typing in the input field and the request is pending.
   * This way we can show a loading spinner while the user is typing.
   */
  const loading = !!searchTerm && isPending;

  return (
    <Autocomplete
      autoComplete="off"
      emptyState={loading ? <AutoCompleteLoading /> : undefined}
      helperText={helperText}
      inputValue={searchTerm}
      items={options}
      label={t('common:glossary.organization')}
      onChange={handleOrganization}
      placeholder={t('common:input.searchPlaceholder', {
        x: `${t('common:name')}, ${t('common:customerNumber')}`,
      })}
      zIndex={1}
      zIndexList={1}
    />
  );
};
